import { Button, LinkButton, Search } from '@pmi/dsm-react';
import { CheckmarkIcon } from '@pmi/dsm-react/dist/components/icons/avatar';
import { SetStateAction, useState } from 'react';
import styles from './chapterSelectionBlock.module.scss';
import { ProductLink } from '../../../../models';

export interface Chapter extends ProductLink {
  chapterRegion?: string;
  chapterCountry?: string;
}

export interface ChapterSelectionBlockProps {
  chapters: Chapter[];
  suggestedChapters: Chapter[];
  className: string;
  selectedChapter: Chapter | null;
  onSearchChapter: (searchMode: boolean) => void;
  onSuggestedSelect: (chapter: Chapter) => void;
  onSearchSelect: (chapter: Chapter) => void;
  isLoading?: boolean;
}

export default function ChapterSelectionBlock({
  className,
  suggestedChapters,
  onSearchChapter,
  chapters = [],
  selectedChapter = null,
  onSuggestedSelect,
  onSearchSelect,
  isLoading = false,
}: ChapterSelectionBlockProps) {
  const [searchText, setSearchText] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isSearch, setIsSearch] = useState(false);
  const [selected, setSelected] = useState<Chapter | null>(selectedChapter);
  const [autoCompleteSearchResult, setAutoCompleteSearchResult] = useState<
    Chapter[]
  >([]);
  const [searchResult, setSearchResult] = useState<Chapter[]>([]);
  const isMatch = (subject: string, value: string) =>
    subject.toLowerCase().includes(value.toLowerCase()) ||
    subject.toLowerCase().indexOf(value.toLowerCase()) >= 0;
  const onTextChange = (e: Event | any) => {
    const text = e.target.value;
    setIsSearch(false);
    setSearchText(text);
    let filtered: SetStateAction<Chapter[]> = [];
    if (text) {
      filtered = chapters.filter((chapter) =>
        isMatch(chapter.title, text?.toString()),
      );
    }
    setAutoCompleteSearchResult(filtered);
    onSearchChapter(false);
  };
  const onSearch = (text: string | number | readonly string[]) => {
    setIsSearch(text.toString().length > 0);
    onSearchChapter(text.toString().length > 0);
    setSearchKeyword(text?.toString());
    const filtered = chapters.filter((chapter) =>
      isMatch(chapter.title, text?.toString()),
    );
    setSearchResult(filtered);
  };

  const onSearchClear = () => {
    setSearchText('');
    setIsSearch(false);
    setAutoCompleteSearchResult([]);
    onSearchChapter(false);
    setSearchResult([]);
    setSearchKeyword('');
  };

  const autocompleteBlock = () => {
    const maxDisplay = 5;
    if (autoCompleteSearchResult.length === 0) {
      return null;
    }
    const result =
      autoCompleteSearchResult.length > maxDisplay
        ? autoCompleteSearchResult.slice(0, maxDisplay)
        : autoCompleteSearchResult;
    return (
      <div className={`shadow ${styles.chapterSearchAutocomplete}`}>
        {result.map((chapter) => (
          <div
            key={`chapter-${chapter.id?.toString()}`}
            className={`${styles.autocompleteItem}`}
          >
            <div className="d-block">
              <span className="d-block">
                <LinkButton
                  titleText={chapter.title}
                  className={`${styles.autocompleteItemTitle} p-0 m-0 text-primary h-auto d-block text-start`}
                  onClick={() => {
                    setAutoCompleteSearchResult([]);
                    setSearchText(chapter.title);
                    onSearch(chapter.title);
                    onSearchSelect(chapter);
                    setSelected(chapter);
                  }}
                />
              </span>
              <span className={`${styles.chapterItemLocation} text-sm`}>
                {[
                  chapter?.chapterCity,
                  chapter?.chapterState,
                  chapter?.chapterRegion,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </span>
            </div>
          </div>
        ))}
        {autoCompleteSearchResult.length > maxDisplay && (
          <div
            key="chapter-show-more"
            className={`${styles.autocompleteChapterShowMore}`}
          >
            <LinkButton
              titleText={`Show all results for '${searchText}'`}
              className="p-2 m-0 h-auto fw-normal"
              onClick={() => {
                onSearch(searchText);
                setAutoCompleteSearchResult([]);
              }}
            />
          </div>
        )}
      </div>
    );
  };
  const chapterBlock = (
    chapter: Chapter,
    onClick: (chapter: Chapter) => void,
    outline = false,
  ) => {
    const isSelectedChapter = chapter?.chapterCode === selected?.chapterCode;
    return (
      <div
        key={`chapter-${chapter?.chapterCode?.toString()}`}
        className={`${styles.itemChapter} ${
          isSelectedChapter ? styles.itemChapterActive : ''
        }`}
      >
        <div className="d-block flex-grow-1">
          <span className={styles.chapterItemTitle}>{chapter.title}</span>
          <span className={`${styles.chapterItemLocation} text-sm`}>
            {chapter?.chapterCity ? `${chapter?.chapterCity}, ` : ''}
            {chapter?.chapterState}
          </span>
          <span className={`${styles.chapterItemLocation} text-sm`}>
            {chapter?.chapterRegion}
          </span>
        </div>
        <div className="d-block">
          <Button
            disabled={isLoading}
            titleText={isSelectedChapter ? 'Selected' : 'Select Chapter'}
            icon={isSelectedChapter ? CheckmarkIcon : null}
            size="xs"
            className={styles.selectButton}
            onClick={() => {
              setSelected(chapter);
              onClick(chapter);
            }}
            variant={
              outline && !isSelectedChapter ? 'outline-secondary' : 'primary'
            }
          />
        </div>
      </div>
    );
  };

  const displayMoreSearchResult = () => (
    <div className={`${styles.searchResultBlock} mt-3 displayMoreSearchResult`}>
      {searchResult.length > 0 && (
        <>
          {searchKeyword ? (
            <p className="mb-2">
              Chapters Matching <strong>{`"${searchKeyword}"`}</strong>
            </p>
          ) : (
            <p className="mb-2">Showing all chapters</p>
          )}
          {searchResult.length > 0 && (
            <div className="d-block">
              <div className={styles.chapterList}>
                {searchResult.map((chapter) =>
                  chapterBlock(
                    chapter,
                    onSearchSelect,
                    selectedChapter
                      ? chapter.chapterCode !== selectedChapter.chapterCode
                      : true,
                  ),
                )}
              </div>
            </div>
          )}
        </>
      )}
      {searchResult.length === 0 && (
        <p className="mb-2">
          No Chapters Matching <strong>{`"${searchKeyword}"`}</strong>
        </p>
      )}
      <div className="d-block mt-3">
        <LinkButton
          titleText="Clear Search"
          className="text-decoration-underline fw-normal"
          onClick={() => onSearchClear()}
        />
      </div>
    </div>
  );

  const hideSearchBar = chapters.length === 2 || chapters.length === 3;

  return (
    <div
      className={`chapter-select-block ${className} ${
        searchText.length > 0 &&
        autoCompleteSearchResult?.length > 0 &&
        styles.chapterAutoCompleteBlock
      }`}
    >
      {!hideSearchBar && (
        <div className="d-block mb-4 position-relative">
          <Search
            id="search-chapter"
            className={`${styles.searchInput} ${
              !isSearch &&
              autoCompleteSearchResult.length > 0 &&
              searchText.length > 0
                ? styles.searchInputHasMatches
                : ''
            }`}
            value={searchText}
            onChange={onTextChange}
            onClear={onSearchClear}
            onClick={() => {
              if (!searchText) {
                onSearch('');
              }
            }}
            placeholder="Search all chapters"
            disabled={chapters.length === 0}
            onSearch={onSearch}
          />
          {searchText.length > 0 &&
            !isSearch &&
            autoCompleteSearchResult.length > 0 &&
            autocompleteBlock()}
          {isSearch && displayMoreSearchResult()}
        </div>
      )}
      {chapters.length === 0 && (
        <p className="pt-0 text-danger">No chapters to select or search.</p>
      )}
      {!isSearch && suggestedChapters && suggestedChapters.length > 0 && (
        <div className="d-block">
          <h4 className="fw-normal">Suggested for you</h4>
          <p className="my-2">
            Explore PMI Chapters to discover and connect with nearby chapters in
            your region for local networking and tailored resources.
          </p>
          <div className={styles.chapterList}>
            {suggestedChapters.map((chapter) => {
              if (selectedChapter) {
                return chapterBlock(
                  chapter,
                  onSuggestedSelect,
                  chapter.chapterCode !== selectedChapter.chapterCode,
                );
              }
              return chapterBlock(chapter, onSuggestedSelect, true);
            })}
          </div>
        </div>
      )}
    </div>
  );
}
